import { SegmentResponse } from "@/graphql/types/graphql";
import { Segment, SegmentationMethod } from "../types/types";
import { SegmentRange, TableSegment } from "../utils/segmentationTableUtils";

interface UseTableSegmentsProps {
  segmentGroups: { index: number; type: string; method: SegmentationMethod; segments: Segment[] }[];
  data: SegmentResponse[] | undefined;
}

interface TableSegments {
  segments: TableSegment[];
  hasOneDataAndOneManualSegment: boolean;
  isManualOnly: boolean;
}

export const useTableSegments = ({ segmentGroups, data }: UseTableSegmentsProps): TableSegments => {
  const sortedSegmentGroups = [...segmentGroups].sort((a, b) => a.type.localeCompare(b.type));

  const segments: TableSegment[] = sortedSegmentGroups
    .map((group, groupIndex) => {
      if (!group.type) return null;

      if (groupIndex === 1 && sortedSegmentGroups.length === 2) {
        const primaryGroup = sortedSegmentGroups[0];
        return {
          index: group.index,
          segmentType: group.type,
          method: group.method,
          ranges:
            data
              ?.filter((d) => d.segment === primaryGroup.type)
              .map((d) => ({
                range: d.segmentRange || "",
                segmentValue: d.segmentRange || "",
                segment: null as TableSegment,
                data: d,
                segmentIndex: group.index,
              })) || [],
        };
      }

      const isDataMethod = group.method === "DATA";
      const ranges: SegmentRange[] = isDataMethod
        ? (data?.filter((d) => d.segment === group.type) || []).reduce<SegmentRange[]>((acc, d) => {
            if (!acc.some((r) => r.range === (d.segmentRange || "Data-Driven") && r.segmentValue === d.segmentValue)) {
              acc.push({
                range: d.segmentRange || "Data-Driven",
                segmentValue: d.segmentValue,
                segment: null,
                data: d,
                segmentIndex: group.index,
              });
            }
            return acc;
          }, [])
        : [
            ...group.segments.map((segment) => {
              const range = `${segment.minValue}-${segment.maxValue}`;
              const matchingData = data?.find(
                (d) =>
                  d.segment === group.type && d.segmentValue === range && (groupIndex === 0 || d.segmentRange === range)
              );
              return {
                range,
                segmentValue: range,
                segment,
                data: matchingData,
                segmentIndex: group.index,
              };
            }),
            {
              range: `${group.segments[group.segments.length - 1]?.maxValue}`,
              segmentValue: `${group.segments[group.segments.length - 1]?.maxValue}`,
              segment: null,
              data: data?.find(
                (d) =>
                  d.segment === group.type &&
                  d.segmentValue === `${group.segments[group.segments.length - 1]?.maxValue}` &&
                  (groupIndex === 0 || d.segmentRange === `${group.segments[group.segments.length - 1]?.maxValue}`)
              ),
              segmentIndex: group.index,
            },
          ];

      return {
        index: group.index,
        segmentType: group.type,
        method: group.method,
        ranges,
      };
    })
    .filter((segment): segment is TableSegment => segment !== null);

  const hasOneDataAndOneManualSegment =
    segments.length === 2 &&
    segments.some((seg) => seg.method === "DATA") &&
    segments.some((seg) => seg.method === "MANUAL");

  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");

  return { segments, hasOneDataAndOneManualSegment, isManualOnly };
};
